"use client";

import { AlertCircle, LoaderCircle, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { CommercialModality } from "@/lib/commercial-plan-presentation";

import PricingCard from "@/components/plans/PricingCard";
import { formatPlanPrice, getPlans } from "@/lib/plan-api";
import {
  getPublicPlatformSettings,
  type PublicPlatformSettings,
} from "@/lib/platform-settings-api";
import { buildPlanFeatures, getMarketingPlan } from "@/lib/marketing-plans";
import type { PlanApi } from "@/types/plan";

type BillingCycle = "monthly" | "yearly";

export default function PricingSection({
  compact = false,
  initialPlans,
  showComparison = false,
  featureAudience,
  modalitySelector,
  embedded = false,
  heading,
}: {
  compact?: boolean;
  initialPlans?: PlanApi[];
  showComparison?: boolean;
  featureAudience?: CommercialModality;
  modalitySelector?: ReactNode;
  embedded?: boolean;
  heading?: string;
}) {
  const Heading = embedded ? "h2" : "h1";
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");

  const [plans, setPlans] = useState<PlanApi[]>(initialPlans ?? []);

  const [platformSettings, setPlatformSettings] =
    useState<PublicPlatformSettings | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isCancelled = false;

    Promise.all([
      initialPlans ? Promise.resolve(initialPlans) : getPlans(),
      getPublicPlatformSettings(),
    ])
      .then(([plansResponse, settingsResponse]) => {
        if (!isCancelled) {
          setPlans(plansResponse);
          setPlatformSettings(settingsResponse);
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Não foi possível carregar os planos.",
          );
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [initialPlans]);

  return (
    <section
      className={
        compact ? undefined : "bg-slate-50 px-5 py-16 lg:px-8 lg:py-24"
      }
    >
      <div className="mx-auto max-w-[1280px]">
        <header className="text-center">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-blue-600">
            {embedded ? "Família Empresas" : "Planos"}
          </p>

          {modalitySelector}

          {compact ? (
            <h2 className="mx-auto mt-3">
              Tecnologia acessível para sua empresa
            </h2>
          ) : (
            <Heading className="mt-3 text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
              {heading ?? "Escolha o plano ideal para sua empresa"}
            </Heading>
          )}

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600">
            {platformSettings?.initial_trial_enabled &&
            platformSettings.initial_trial_plan_name ? (
              <>
                Comece com {platformSettings.initial_trial_days}{" "}
                {platformSettings.initial_trial_days === 1
                  ? "dia grátis"
                  : "dias grátis"}{" "}
                no plano {platformSettings.initial_trial_plan_name}. Escolha
                depois o plano ideal para continuar crescendo.
              </>
            ) : (
              <>
                Escolha o plano ideal para sua empresa e comece a divulgar seus
                produtos e serviços.
              </>
            )}
          </p>

          <div className="mx-auto mt-8 inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              aria-pressed={billingCycle === "monthly"}
              onClick={() => setBillingCycle("monthly")}
              className={`h-10 rounded-xl px-5 text-sm font-bold transition ${
                billingCycle === "monthly"
                  ? "bg-blue-600 text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              Mensal
            </button>

            <button
              type="button"
              aria-pressed={billingCycle === "yearly"}
              onClick={() => setBillingCycle("yearly")}
              className={`h-10 rounded-xl px-5 text-sm font-bold transition ${
                billingCycle === "yearly"
                  ? "bg-blue-600 text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              Anual
            </button>
          </div>
        </header>

        {isLoading ? (
          <div className="flex min-h-80 items-center justify-center">
            <div className="flex items-center gap-3 text-sm font-bold text-slate-500">
              <LoaderCircle size={21} className="animate-spin" />
              Carregando planos...
            </div>
          </div>
        ) : errorMessage ? (
          <div className="mx-auto mt-12 flex max-w-xl flex-col items-center rounded-3xl border border-red-200 bg-white p-8 text-center shadow-sm">
            <AlertCircle size={38} className="text-red-500" />

            <p className="mt-4 text-sm font-bold text-red-700">
              {errorMessage}
            </p>
          </div>
        ) : (
          <div className="mx-auto mt-14 grid max-w-[1280px] items-stretch gap-7 md:grid-cols-2 xl:grid-cols-3">
            {plans.map((plan) => {
              const marketing = getMarketingPlan(plan.slug);

              if (!marketing) {
                return null;
              }

              const features = buildPlanFeatures(plan, featureAudience);

              const price =
                billingCycle === "yearly"
                  ? formatPlanPrice(plan.yearly_price)
                  : formatPlanPrice(plan.monthly_price);

              const period = billingCycle === "yearly" ? "/ano" : "/mês";

              return (
                <PricingCard
                  key={plan.id}
                  compact={compact || showComparison}
                  name={plan.name}
                  description={plan.description}
                  price={price}
                  period={period}
                  features={features}
                  buttonLabel={
                    platformSettings?.initial_trial_enabled &&
                    platformSettings.initial_trial_plan_name === plan.name
                      ? `Testar grátis por ${platformSettings.initial_trial_days} ${
                          platformSettings.initial_trial_days === 1
                            ? "dia"
                            : "dias"
                        }`
                      : marketing.buttonLabel
                  }
                  href={`/cadastro?plan=${plan.slug}&billing=${billingCycle}`}
                  highlighted={marketing.highlighted}
                  primaryAction={
                    platformSettings?.initial_trial_enabled === true &&
                    platformSettings.initial_trial_plan_name === plan.name
                  }
                  badge={marketing.badge}
                />
              );
            })}
          </div>
        )}
        {showComparison && !isLoading && !errorMessage && plans.length > 0 && (
          <details className="plan-comparison">
            <summary>
              Compare a capacidade dos planos <ChevronDown size={22} />
            </summary>
            <div
              className="plan-table"
              role="region"
              aria-label="Comparação dos planos"
              tabIndex={0}
            >
              <table>
                <thead>
                  <tr>
                    <th scope="col">Recurso</th>
                    {plans
                      .filter((plan) => getMarketingPlan(plan.slug))
                      .map((plan) => (
                        <th scope="col" key={plan.id}>
                          {plan.name}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ["Produtos e anúncios", "product_limit"],
                      ["Categorias", "category_limit"],
                      ["Imagens por item", "image_limit_per_product"],
                      ["Itens em destaque", "featured_product_limit"],
                      ["Administradores", "admin_limit"],
                    ] as const
                  ).map(([label, field]) => (
                    <tr key={field}>
                      <th scope="row">{label}</th>
                      {plans
                        .filter((plan) => getMarketingPlan(plan.slug))
                        .map((plan) => (
                          <td key={plan.id}>
                            {plan[field] == null
                              ? "Sem limite"
                              : plan[field].toLocaleString("pt-BR")}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </section>
  );
}
